<template>
  <div class="setup-hub-sub-step-details" v-if="localValue !== null">
    <div class="setup-hub-sub-step-details--header">
      <div class="coro-link body2" @click="goToList()">‹ {{ $t("setupHub.title") }}</div>
      <div class="d-flex align-center justify-space-between mt-2">
        <div class="headline6">
          {{ $t(`setupHub.subSteps.${localValue.step}.title`) }}
        </div>
        <div class="body2 text-indigo-medium text-no-wrap">
          ~{{ $t(`setupHub.subSteps.${localValue.step}.time`) }}
        </div>
      </div>

      <div class="body3 text-indigo-medium mt-2">
        {{ $t(`setupHub.subSteps.${localValue.step}.subtitle`) }}
      </div>
    </div>

    <div class="setup-hub-sub-step-details--content">
      <setup-hub-conditional-banner
        :sub-step="localValue"
        @switch-to-step="onConditionalStepClick"
      />
      <component :is="subStepToComponentMap[localValue.step]" :subStep="localValue.step" />
    </div>

    <div
      class="setup-hub-sub-step-details--footer"
      :class="{ 'setup-hub-sub-step-details--footer--space-between': !isInProgress }"
    >
      <div v-if="isInProgress" class="setup-hub-sub-step-details--footer--btn" @click="skipStep()">
        {{ $t("setupHub.skipStep") }}
      </div>
      <setup-hub-completed-sub-step-footer-component
        v-else
        :status="localValue.status"
        @start-new-task-click="goToList()"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { type Component, defineComponent, type PropType, ref, onMounted, computed } from "vue";
import { SetupHubStatus, SetupHubStep } from "@/constants/setup-hub.ts";
import GenericStepContent from "@/components/setup-hub/sidebar/content-components/GenericStepContent.vue";
import SetupHubAddUsersToProtectionContent from "@/components/setup-hub/sidebar/content-components/SetupHubAddUsersToProtectionContent.vue";
import { type SetupHubStepItem, useSetupHubStore } from "@/_store/setup-hub.module.ts";
import DeployAgentsContent from "@/components/setup-hub/sidebar/content-components/DeployAgentsContent.vue";
import SetupHubConditionalBanner from "@/components/setup-hub/sidebar/content-components/SetupHubConditionalBanner.vue";
import SetupHubCompletedSubStepFooterComponent from "@/components/setup-hub/sidebar/content-components/SetupHubCompletedSubStepFooterComponent.vue";
import cloneDeep from "lodash/cloneDeep";
import { handleNavigationInSetupHub, trackGtmEvent } from "@/_helpers/utils.ts";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";

export default defineComponent({
  components: { SetupHubConditionalBanner, SetupHubCompletedSubStepFooterComponent },
  props: {
    subStep: {
      type: Object as PropType<SetupHubStepItem>,
      required: true,
    },
  },
  emits: ["back-button-clicked"],
  setup(props, { emit }) {
    const localValue = ref<SetupHubStepItem | null>(null);
    const { skipOnboardingStep, findStep, getOnboardingStatus } = useSetupHubStore();
    const subStepToComponentMap: Record<SetupHubStep, Component> = {
      [SetupHubStep.INVITE_TEAMMATES]: GenericStepContent,
      [SetupHubStep.CONNECT_CLOUD_APP]: GenericStepContent,
      [SetupHubStep.ADD_PROTECTED_USERS]: SetupHubAddUsersToProtectionContent,
      [SetupHubStep.SETUP_ACCESS_PERMISSIONS]: GenericStepContent,
      [SetupHubStep.DEPLOY_ENDPOINT_AGENT]: DeployAgentsContent,
      [SetupHubStep.ACTIVATE_SAT]: GenericStepContent,
      [SetupHubStep.ADD_DEVICE_LABELS]: GenericStepContent,
      [SetupHubStep.ADD_DEVICE_POSTURE_SETTINGS]: GenericStepContent,
      [SetupHubStep.DEFINE_DEVICE_DATA_MONITORING_RULES]: GenericStepContent,
      [SetupHubStep.DEFINE_USER_DATA_MONITORING_RULES]: GenericStepContent,
      [SetupHubStep.CREATE_NETWORK_VIRTUAL_SERVER]: GenericStepContent,
      [SetupHubStep.SETUP_VPN_ACCESS]: GenericStepContent,
      [SetupHubStep.CREATE_SWG_VIRTUAL_SERVER]: GenericStepContent,
      [SetupHubStep.ENABLE_DNS_FILTERING]: GenericStepContent,
    };

    const isInProgress = computed(() => {
      if (!localValue.value) {
        return false;
      }
      return (
        localValue.value.status === SetupHubStatus.NEW ||
        localValue.value.status === SetupHubStatus.IN_PROGRESS
      );
    });

    const goToList = () => {
      emit("back-button-clicked");
    };

    const skipStep = async () => {
      trackGtmEvent(
        GtmAction.SETUP_HUB,
        `${startCase(localValue.value?.step)} - ${GTMParams.SKIPPED}`
      );
      emit("back-button-clicked");
      await skipOnboardingStep(props.subStep.step);
    };

    const onConditionalStepClick = async (step: SetupHubStep) => {
      await getOnboardingStatus();
      const data = findStep(step);
      if (!data) {
        emit("back-button-clicked");
        return;
      }
      localValue.value = data;
      handleNavigationInSetupHub(localValue.value);
    };

    onMounted(() => {
      localValue.value = cloneDeep(props.subStep);
    });

    return {
      goToList,
      subStepToComponentMap,
      skipStep,
      onConditionalStepClick,
      localValue,
      isInProgress,
    };
  },
});
</script>

<style lang="scss" scoped>
.setup-hub-sub-step-details {
  height: 100%;
  display: flex;
  flex-direction: column;
  &--header {
    background-color: rgb(var(--v-theme-indigo-faint));
    padding: 16px;
    flex-shrink: 0;
  }
  &--footer {
    background-color: rgb(var(--v-theme-indigo-faint));
    min-height: 52px;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    &--space-between {
      justify-content: space-between;
    }
    &--btn {
      cursor: pointer;
      color: rgb(var(--v-theme-primary));
      font-weight: 600;
      font-size: 14px;
      line-height: 130%;
      letter-spacing: 1px;
      text-transform: uppercase;
    }
  }
  &--content {
    padding: 16px;
    background-color: rgb(var(--v-theme-white));
    flex: 1;
    min-height: 0;
    overflow: auto;
  }
}
</style>
